/**
 * Pure helpers for `useScheduleWithAiForm` (no React). Keeps the hook file focused on state and effects.
 */
import moment from 'moment-timezone';
import { SCHEDULE_AI_COPY } from '../../constants';
import { COORDINATOR_LOCATION_OPTIONS, TIME_OPTIONS_15MIN } from '../options';

const COPY = SCHEDULE_AI_COPY;

/** First location value that is enabled in availableLocations (for defaulting when plan has no location). */
export function getFirstAvailableLocation(availableLocations: unknown): string {
  const locationsMap =
    (availableLocations as { locations?: Record<string, boolean> })
      ?.locations ?? (availableLocations as Record<string, boolean>);
  if (!locationsMap || typeof locationsMap !== 'object') return '';
  const first = COORDINATOR_LOCATION_OPTIONS.find(
    (opt) => (locationsMap as Record<string, boolean>)[opt.value] === true
  );
  return first?.value ?? '';
}

type OfficeRow = { id: number; name?: string };

export function buildDisplayPlan(params: {
  existingSession:
    | { interview_config?: Record<string, unknown> }
    | null
    | undefined;
  existingInterview: Record<string, unknown> | null | undefined;
  interviewPlan: Record<string, unknown> | null | undefined;
  planInterviewers: { id: number; [key: string]: unknown }[];
  offices: OfficeRow[];
}): Record<string, unknown> {
  const {
    existingSession,
    existingInterview,
    interviewPlan,
    planInterviewers,
    offices,
  } = params;

  const resolveOfficeName = (atsOfficeId: number | null | undefined) => {
    if (atsOfficeId === null) return undefined;
    const office = offices?.find((o) => o.id === atsOfficeId);
    return office?.name;
  };

  if (existingSession?.interview_config) {
    const c = existingSession.interview_config;
    const participantIds = (c.participant_ids as number[]) || [];
    const loc = (c.location as string) ?? interviewPlan?.location;
    const atsOfficeId = (c as { ats_office_id?: number }).ats_office_id;
    return {
      ...(interviewPlan || {}),
      name: (c.name as string) ?? interviewPlan?.name,
      duration: (c.duration_minutes as number) ?? interviewPlan?.duration,
      kind: (c.kind as string) ?? interviewPlan?.kind,
      interview_kind:
        (c.kind as string) ?? (interviewPlan?.interview_kind as string),
      location: loc,
      ...(loc === 'onsite' && {
        ats_office_id: atsOfficeId,
        office_name: resolveOfficeName(atsOfficeId),
      }),
      calendar_visibility:
        c.calendar_visibility === 'private'
          ? 'private'
          : ((interviewPlan?.calendar_visibility as string) ?? 'default'),
      note_taker_enabled:
        (c.note_taker_enabled as boolean) ?? interviewPlan?.note_taker_enabled,
      interviewers: planInterviewers.filter((p) =>
        participantIds.includes(p.id)
      ),
      participants: planInterviewers.filter((p) =>
        participantIds.includes(p.id)
      ),
    };
  }

  if (existingInterview && !existingSession) {
    const inv = existingInterview;
    const duration =
      (inv.duration_minutes as number) ??
      (inv.start_time && inv.end_time
        ? Math.max(
            15,
            moment(inv.end_time as string).diff(
              moment(inv.start_time as string),
              'minutes'
            )
          )
        : undefined) ??
      interviewPlan?.duration;
    return {
      ...(interviewPlan || {}),
      duration,
      kind:
        (inv.kind as string) ??
        (inv.interview_kind as string) ??
        interviewPlan?.kind,
      interview_kind:
        (inv.interview_kind as string) ??
        (inv.kind as string) ??
        (interviewPlan?.interview_kind as string),
      location: (inv.location as string) ?? interviewPlan?.location,
      ...(((inv.location as string) ?? interviewPlan?.location) ===
        'onsite' && {
        ats_office_id: (inv as { ats_office_id?: number }).ats_office_id,
        office_name: resolveOfficeName(
          (inv as { ats_office_id?: number }).ats_office_id
        ),
      }),
      calendar_visibility:
        inv.calendar_visibility === 'private' ? 'private' : 'default',
      note_taker_enabled: (inv.note_taker_enabled as boolean) ?? false,
      interviewers: (inv.interviewers as unknown[]) ?? [],
      participants: (inv.interviewers as unknown[]) ?? [],
    };
  }

  const plan = interviewPlan || {};
  const planLoc = plan.location as string;
  const planOfficeId = (plan as { ats_office_id?: number }).ats_office_id;
  return {
    ...plan,
    ...(planLoc === 'onsite' && {
      ats_office_id: planOfficeId,
      office_name: resolveOfficeName(planOfficeId),
    }),
  };
}

/** Single footer line when contact has a valid IANA `time_zone`. */
export function buildCandidateCurrentTimeLine(
  hasCandidateTimezone: boolean,
  candidateCurrentTime: string
): string | null {
  if (!hasCandidateTimezone) return null;
  return `${COPY.currentTimeForCandidate}: ${candidateCurrentTime}`;
}

/**
 * Maps a coordinator session UTC ISO time to custom timing state (15-min slot in picker TZ).
 */
export function parseCoordinatorScheduledAtToCustomSchedule(
  iso: string | null | undefined,
  timezoneKey: string
): { date: Date; time: string; timezoneKey: string } | null {
  if (!iso) return null;
  const m = moment.utc(iso).tz(timezoneKey);
  if (!m.isValid()) return null;
  const allowed = new Set(TIME_OPTIONS_15MIN.map((o) => o.value));
  let candidate = m.format('HH:mm');
  if (!allowed.has(candidate)) {
    const totalMinutes = m.hours() * 60 + m.minutes();
    const rounded = Math.round(totalMinutes / 15) * 15;
    const total = Math.min(Math.max(rounded, 0), 23 * 60 + 45);
    const h = Math.floor(total / 60);
    const min = total % 60;
    candidate = `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
  }
  if (!allowed.has(candidate)) {
    const target = m.hours() * 60 + m.minutes();
    let best = TIME_OPTIONS_15MIN[0]?.value ?? '09:00';
    let bestDiff = Infinity;
    for (const opt of TIME_OPTIONS_15MIN) {
      const [hh, mm] = opt.value.split(':').map(Number);
      const mins = hh * 60 + mm;
      const diff = Math.abs(mins - target);
      if (diff < bestDiff) {
        bestDiff = diff;
        best = opt.value;
      }
    }
    candidate = best;
  }
  const ymd = m.format('YYYY-MM-DD');
  const dayStart = moment.tz(ymd, 'YYYY-MM-DD', timezoneKey).startOf('day');
  return {
    date: dayStart.toDate(),
    time: candidate,
    timezoneKey,
  };
}

/** @deprecated Prefer account-format display from useDateFormat in useScheduleWithAiForm. Kept for tests / callers. */
export function computeCustomScheduleDisplay(
  customSchedule: {
    date?: unknown;
    time?: string;
    timezoneKey?: string;
  } | null
): string | null {
  if (
    !customSchedule?.date ||
    !customSchedule?.time ||
    !customSchedule?.timezoneKey
  )
    return null;
  const m = moment.tz(
    `${moment(customSchedule.date).format('YYYY-MM-DD')} ${customSchedule.time}`,
    customSchedule.timezoneKey
  );
  return `${m.format('MMM D, YYYY [at] h:mm A')} ${m.format('z')}`;
}

/** Preset slots use `candidateTimezoneKey` (candidate when valid, else recruiter). */
export function computeScheduledAtISO(params: {
  timing: string;
  customSchedule: {
    date?: unknown;
    time?: string;
    timezoneKey?: string;
  } | null;
  candidateTimezoneKey: string;
}): string | null {
  const { timing, customSchedule, candidateTimezoneKey } = params;
  if (timing === 'immediately') return null;
  if (
    timing === 'custom' &&
    customSchedule?.date &&
    customSchedule?.time &&
    customSchedule?.timezoneKey
  ) {
    return moment
      .tz(
        `${moment(customSchedule.date).format('YYYY-MM-DD')} ${customSchedule.time}`,
        customSchedule.timezoneKey
      )
      .utc()
      .toISOString();
  }
  const anchor = moment().tz(candidateTimezoneKey);
  let scheduledMoment;
  switch (timing) {
    case 'today_evening':
      scheduledMoment = anchor.clone().startOf('day').add(18, 'hours');
      break;
    case 'tomorrow_morning':
      scheduledMoment = anchor
        .clone()
        .add(1, 'day')
        .startOf('day')
        .add(9, 'hours');
      break;
    case 'tomorrow_afternoon':
      scheduledMoment = anchor
        .clone()
        .add(1, 'day')
        .startOf('day')
        .add(14, 'hours');
      break;
    case 'tomorrow_evening':
      scheduledMoment = anchor
        .clone()
        .add(1, 'day')
        .startOf('day')
        .add(18, 'hours');
      break;
    default:
      return null;
  }
  return scheduledMoment ? scheduledMoment.utc().toISOString() : null;
}
